import React from 'react';
import {
  Box,
  Fade,
  MobileStepper,
  Paper,
  Typography,
} from '@mui/material';
import { useIntercom } from 'react-use-intercom';
import type { Partner } from '../../components/LeadFormIndex/types';
import { useQuery } from '../../utils/helpers';
import RegistrationForm from './RegistrationForm';
import womSlide1 from '../../assets/bnpl_wom_1.webp';
import womSlide2 from '../../assets/bnpl_wom_2.webp';
import womSlide3 from '../../assets/bnpl_wom_3.webp';

const carouselSlides = [
  {
    image: 'https://assets.banca.me/bnpl/bnpl_bunkers.png',
    title: 'Tú tienes el control: elige cómo pagar',
  },
  {
    image: 'https://assets.banca.me/bnpl/bnpl_viajes.png',
    title: 'Tus compras en cuotas, sin usar tarjeta',
  },
  {
    image: 'https://assets.banca.me/bnpl/bnpl_compras.png',
    title: 'Compra hoy, paga a tu propio ritmo',
  },
];

const womCarouselSlides = [
  { ...carouselSlides[0], image: womSlide1 },
  { ...carouselSlides[1], image: womSlide2 },
  { ...carouselSlides[2], image: womSlide3 },
];

const isWomOrigin = (origin?: string) => {
  const normalizedOrigin = origin?.toLowerCase();
  if (!normalizedOrigin) return false;
  return normalizedOrigin === 'wom'
    || normalizedOrigin.endsWith('-wom')
    || normalizedOrigin.includes('wom-');
};

const bnplPartner: Partner = {
  partnerName: 'Banca.Me',
  products: ['consumer', 'microLoan'],
  config: {
    styles: {
      primaryColor: '#755DDF',
      secondaryColor: '#755DDF',
    },
  },
};

const SLIDE_INTERVAL_MS = 7500;
const FADE_DURATION_MS = 1200;

export default function BNPLRegistration() {
  const {
    boot: bootIntercom,
    shutdown: shutdownIntercom,
  } = useIntercom();
  const origin = useQuery().get('origin') || undefined;
  const slides = isWomOrigin(origin) ? womCarouselSlides : carouselSlides;
  const [activeStep, setActiveStep] = React.useState(0);
  const [isSlideVisible, setIsSlideVisible] = React.useState(true);
  const fadeTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    bootIntercom();
    return () => shutdownIntercom();
  }, []);

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      if (fadeTimeoutRef.current) {
        window.clearTimeout(fadeTimeoutRef.current);
      }
      setIsSlideVisible(false);
      fadeTimeoutRef.current = window.setTimeout(() => {
        setActiveStep((currentStep) => (currentStep + 1) % slides.length);
        setIsSlideVisible(true);
      }, FADE_DURATION_MS);
    }, SLIDE_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      if (fadeTimeoutRef.current) {
        window.clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, [slides.length]);

  return (
    <Paper
      square={false}
      elevation={0}
      sx={{
        borderRadius: 5,
        maxWidth: 420,
        margin: '0 auto',
        padding: '30px 30px',
      }}
    >
      <Fade in={isSlideVisible} timeout={FADE_DURATION_MS}>
        <Typography
          variant="h5"
          align="center"
          color="primary"
          sx={{
            marginBottom: 1,
            minHeight: 64,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {slides[activeStep].title}
        </Typography>
      </Fade>
      <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 5, marginBottom: 1 }}>
        <Fade in={isSlideVisible} timeout={FADE_DURATION_MS}>
          <Box
            component="img"
            src={slides[activeStep].image}
            alt={`bnpl-slide-${activeStep + 1}`}
            sx={{
              display: 'block',
              width: '100%',
              height: 200,
              objectFit: 'cover',
            }}
          />
        </Fade>
      </Paper>

      <MobileStepper
        variant="dots"
        steps={slides.length}
        position="static"
        activeStep={activeStep}
        nextButton={<Box />}
        backButton={<Box />}
        sx={{ justifyContent: 'center', marginBottom: 5 }}
      />

      <RegistrationForm partner={bnplPartner} origin={origin} />
    </Paper>
  );
}
